/**
 * useAddProductToCart — one-tap "add" helper used by the marketplace
 * storefront product cards (iter56). Adds a product to the cart with
 * the product's parent shop_id. If the cart already has items from
 * another shop, prompts the user to swap.
 *
 * Returns { add(product), busy } where ``add`` is async-safe.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "./api";
import { useAuth } from "../contexts/AuthContext";

export function useAddProductToCart() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);

    const add = useCallback(async (product) => {
        if (!user) {
            nav("/login", { state: { from: window.location.pathname || "/" } });
            return false;
        }
        const sid = product.restaurant_id || product.shop_id;
        if (!sid) {
            toast.error("This product is not available for ordering");
            return false;
        }
        const item = {
            product_id: product.id,
            name: product.name,
            quantity: 1,
            selling_price: product.selling_price,
            image_url: (product.images || [])[0],
            food_type: product.food_type,
        };
        setBusy(true);
        try {
            try {
                await api.post(`/cart/items?restaurant_id=${sid}`, item);
                window.dispatchEvent(new Event("cart:update"));
                toast.success(`Added ${product.name}`);
                return true;
            } catch (e) {
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                if (status === 409 && detail && typeof detail === "object"
                    && detail.code === "DIFFERENT_RESTAURANT") {
                    const existing = detail.existing_restaurant || "another shop";
                    const ok = window.confirm(
                        `Your cart has items from ${existing}.\n\nWe can only deliver from one shop at a time. Replace your cart with this item?`
                    );
                    if (!ok) return false;
                    await api.post(`/cart/items?restaurant_id=${sid}&force=true`, item);
                    window.dispatchEvent(new Event("cart:update"));
                    toast.success(`Cart cleared. Added ${product.name}`);
                    return true;
                }
                toast.error(typeof detail === "string" ? detail : "Failed to add to cart");
                return false;
            }
        } finally {
            setBusy(false);
        }
    }, [user, nav]);

    return { add, busy };
}
